import { Location } from "react-router-dom";

/**
 * Returns whether the current location should treat `requiredRoute` as active.
 *
 * Segment-safe: matches an exact path or a nested child under that path
 * (`requiredRoute/…`). Does **not** match sibling routes that only share a
 * string prefix (e.g. `/recon` must not match `/recon_adjustments`).
 *
 * Prefer this over `pathname.includes(route)`.
 *
 * @param location - React Router location (uses `pathname` only)
 * @param requiredRoute - Route prefix to test, e.g. `"/recon"` or `"/fp-console"`
 * @returns `true` if pathname equals `requiredRoute` or starts with `requiredRoute + "/"`
 *
 * @example Exact list page
 * ```ts
 * matchCurrentPath({ pathname: "/recon" } as Location, "/recon"); // true
 * ```
 *
 * @example Nested detail under the same feature
 * ```ts
 * matchCurrentPath({ pathname: "/recon/rec_abc" } as Location, "/recon"); // true
 * ```
 *
 * @example Sibling route — no false positive
 * ```ts
 * matchCurrentPath({ pathname: "/recon_adjustments" } as Location, "/recon"); // false
 * matchCurrentPath(
 *   { pathname: "/deposit_receipt_adjustments" } as Location,
 *   "/deposit_receipts"
 * ); // false
 * ```
 *
 * @example Console shell
 * ```ts
 * matchCurrentPath({ pathname: "/fp-console/health-metrics" } as Location, "/fp-console"); // true
 * ```
 *
 * @see docs/NAV_PATH_MATCHING.md
 */
export const matchCurrentPath = (location: Location, requiredRoute: string): boolean => {
  const currentPath = location.pathname;
  return currentPath === requiredRoute || currentPath.startsWith(`${requiredRoute}/`);
};
